import {
  faArrowLeft,
  faSave,
  faUser,
  faIdCard,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faFileText,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import NotificationModal from '../components/NotificationModal';
import usePacientes from '../hooks/usePacientes';
import { useAtendimentos } from '../hooks/useAtendimentos';
import { useAuth } from '../hooks/useAuth';
import Masks from '../shared/masks';
import { Paciente } from '../types';
import Button from '../components/Button';
import FormInput from '../components/FormInput';
import FormTextarea from '../components/FormTextarea';
import FormSelect from '../components/FormSelect';
import PageHeader from '../components/PageHeader';
import Alert from '../components/Alert';
import { pacienteSchema, PacienteFormData } from '../schemas';

interface NotificationState {
  type: 'success' | 'error';
  title: string;
  message: string;
}

interface MaskFunctions {
  CpfMask: (value: string) => string;
  RgMask: (value: string) => string;
  CellMask: (value: string) => string;
  PhoneMask: (value: string) => string;
  Cell2Mask: (value: string) => string;
  PostalCodeMask: (value: string) => string;
}

const emptyPaciente: Paciente = {
  id: '',
  profissao: '',
  celular: '',
  nome: '',
  convenio: '',
  cep: '',
  telefone: '',
  logradouro: '',
  celular_ii: '',
  cpf: '',
  numero: '',
  email: '',
  data_nasc: '',
  complemento: '',
  obs: '',
  rg: '',
  bairro: '',
  estado_civil: '',
  municipio: '',
  uf: ''
};

const emptyForm: PacienteFormData = {
  profissao: '',
  celular: '',
  nome: '',
  convenio: '',
  cep: '',
  telefone: '',
  logradouro: '',
  celular_ii: '',
  cpf: '',
  numero: '',
  email: '',
  data_nasc: '',
  complemento: '',
  obs: '',
  rg: '',
  bairro: '',
  estado_civil: '',
  municipio: '',
  uf: ''
};

export default function PacienteById() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const { paciente = emptyPaciente, error, getById, upsert, loading } = usePacientes();
  const { addToQueue } = useAtendimentos(currentOrganization?.id || '');
  const { CpfMask, RgMask, CellMask, PhoneMask, Cell2Mask, PostalCodeMask }: MaskFunctions =
    Masks();
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Extract queue parameters from URL
  const fromQueue = searchParams.get('fromQueue') === 'true';
  const isPriority = searchParams.get('isPriority') === 'true';
  const searchTerm = searchParams.get('searchTerm') || '';
  const pendingQueueData = fromQueue ? { isPriority, fromQueue } : null;

  const isEditing = id !== 'create';

  // Calculate initial form values based on search term
  const getInitialFormValues = (): PacienteFormData => {
    if (!isEditing && searchTerm) {
      const isValidCPF = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(searchTerm.replace(/\D/g, ''));

      if (isValidCPF) {
        return { ...emptyForm, cpf: searchTerm };
      } else {
        return { ...emptyForm, nome: searchTerm };
      }
    }
    return emptyForm;
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useForm<PacienteFormData>({
    resolver: zodResolver(pacienteSchema),
    mode: 'onChange',
    defaultValues: getInitialFormValues()
  });

  useEffect(() => {
    if (id && id !== 'create') {
      getById(id);
    } else if (!isEditing && searchTerm) {
      // Pre-fill form with search term when creating new patient
      const isValidCPF = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(searchTerm.replace(/\D/g, ''));

      if (isValidCPF) {
        reset({ ...emptyForm, cpf: searchTerm });
      } else {
        reset({ ...emptyForm, nome: searchTerm });
      }
    }
  }, [id, getById, isEditing, searchTerm, reset]);

  // Reset form when paciente data is loaded or when search params change
  useEffect(() => {
    if (paciente && Object.keys(paciente).length > 0 && isEditing) {
      reset(paciente);
    } else if (!isEditing && searchTerm) {
      // Force re-initialize form with search term
      const isValidCPF = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(searchTerm.replace(/\D/g, ''));

      if (isValidCPF) {
        reset({ ...emptyForm, cpf: searchTerm });
      } else {
        reset({ ...emptyForm, nome: searchTerm });
      }
    } else if (!isEditing && !searchTerm) {
      reset(emptyForm);
    }
  }, [paciente, reset, isEditing, searchTerm]);

  const onSubmit = async (
    data: PacienteFormData,
    action: 'save' | 'saveAndBack' | 'saveAndAddToQueue' = 'save'
  ): Promise<void> => {
    setIsSubmitting(true);

    try {
      const pacienteData: Paciente = {
        ...data,
        id: isEditing ? paciente.id : ''
      };

      const result: Paciente | undefined = await upsert(pacienteData);
      if (result) {
        let successMessage = `Paciente ${isEditing ? 'atualizado' : 'cadastrado'} com sucesso.`;

        // If we need to add to queue
        if (action === 'saveAndAddToQueue' && pendingQueueData && currentOrganization?.id) {
          try {
            const queueResponse = await addToQueue(result.id, pendingQueueData.isPriority);
            if (queueResponse.success) {
              successMessage += ' Paciente adicionado à fila de atendimento.';
            } else {
              throw new Error('Erro ao adicionar à fila: ' + queueResponse.message);
            }
          } catch (queueError) {
            console.error('Queue error:', queueError);
            successMessage += ' Erro ao adicionar à fila.';
          }
        }

        setNotification({
          type: 'success',
          title: 'Sucesso!',
          message: successMessage
        });

        setTimeout(() => {
          if (action === 'saveAndBack') {
            navigate('/pacientes');
          } else if (action === 'saveAndAddToQueue') {
            navigate('/atendimentos/fila');
          } else {
            navigate(`/pacientes/${result.id}`);
          }
        }, 1500);
      }
    } catch (error) {
      setNotification({
        type: 'error',
        title: 'Erro ao Salvar',
        message: (error as Error).message || 'Ocorreu um erro inesperado. Tente novamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = handleSubmit(data => onSubmit(data, 'save'));
  const handleSaveAndBack = handleSubmit(data => onSubmit(data, 'saveAndBack'));
  const handleSaveAndAddToQueue = handleSubmit(data => onSubmit(data, 'saveAndAddToQueue'));
  const handleBack = () => {
    navigate('/pacientes');
  };

  if (loading && isEditing) {
    return (
      <main className="w-full flex-grow p-6">
        <div className="text-center py-12">
          <img className="w-12 h-12 mx-auto mb-4" src="/loading.svg" alt="loading" />
          <p className="text-gray-600">Carregando dados do paciente...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full flex-grow p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <PageHeader
          title={isEditing ? 'Editar Paciente' : 'Novo Paciente'}
          subtitle={
            isEditing
              ? 'Atualize as informações do paciente'
              : pendingQueueData
                ? 'Cadastre um novo paciente e adicione à fila de atendimento'
                : 'Cadastre um novo paciente no sistema'
          }
          backLink="/pacientes"
        />

        {pendingQueueData && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Modo: Cadastro para Fila</strong>
                  <br />
                  Após salvar, o paciente será automaticamente adicionado à fila de atendimento
                  {pendingQueueData.isPriority ? ' com prioridade' : ''}.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={e => e.preventDefault()} className="space-y-8">
          {/* Personal Information Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faUser} className="text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Dados Pessoais</h2>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* First row: Nome, Convênio, RG */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-6">
                  <FormInput
                    name="nome"
                    control={control}
                    id="nome"
                    label="Nome Completo"
                    required
                    type="text"
                    placeholder="Digite o nome completo"
                  />
                </div>
                <div className="md:col-span-3">
                  <FormInput
                    name="convenio"
                    control={control}
                    id="convenio"
                    label="Convênio"
                    type="text"
                    placeholder="Nome do convênio"
                  />
                </div>
                <div className="md:col-span-3">
                  <FormInput
                    name="rg"
                    control={control}
                    id="rg"
                    label="RG"
                    type="text"
                    maxLength={15}
                    maskFn={RgMask}
                    placeholder="00.000.000-0"
                  />
                </div>
              </div>

              {/* Second row: Email, CPF, Data Nascimento */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-6">
                  <FormInput
                    name="email"
                    control={control}
                    id="email"
                    label="Email"
                    leftIcon={faEnvelope}
                    type="email"
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div className="md:col-span-3">
                  <FormInput
                    name="cpf"
                    control={control}
                    id="cpf"
                    label="CPF"
                    leftIcon={faIdCard}
                    required
                    type="text"
                    maxLength={14}
                    maskFn={CpfMask}
                    placeholder="000.000.000-00"
                  />
                </div>
                <div className="md:col-span-3">
                  <FormInput
                    name="data_nasc"
                    control={control}
                    id="data_nasc"
                    label="Data de Nascimento"
                    required
                    type="date"
                  />
                </div>
              </div>

              {/* Third row: Profissão, Estado Civil */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormInput
                    name="profissao"
                    control={control}
                    id="profissao"
                    label="Profissão"
                    type="text"
                    placeholder="Profissão do paciente"
                  />
                </div>
                <div>
                  <FormSelect
                    name="estado_civil"
                    control={control}
                    id="estado_civil"
                    label="Estado Civil"
                    options={[
                      { value: 'Solteiro(a)', label: 'Solteiro(a)' },
                      { value: 'Casado(a)', label: 'Casado(a)' },
                      { value: 'Divorciado(a)', label: 'Divorciado(a)' },
                      { value: 'Viúvo(a)', label: 'Viúvo(a)' },
                      { value: 'União Estável', label: 'União Estável' }
                    ]}
                    placeholder="Selecione..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faPhone} className="text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Contato</h2>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FormInput
                    name="celular"
                    control={control}
                    id="celular"
                    label="Celular"
                    leftIcon={faPhone}
                    required
                    type="tel"
                    maxLength={15}
                    maskFn={CellMask}
                    placeholder="(00) 90000-0000"
                  />
                </div>
                <div>
                  <FormInput
                    name="telefone"
                    control={control}
                    id="telefone"
                    label="Telefone"
                    type="tel"
                    maxLength={14}
                    maskFn={PhoneMask}
                    placeholder="(00) 0000-0000"
                  />
                </div>
                <div>
                  <FormInput
                    name="celular_ii"
                    control={control}
                    id="celular_ii"
                    label="2º Celular"
                    type="tel"
                    maxLength={15}
                    maskFn={Cell2Mask}
                    placeholder="(00) 90000-0000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                <h2 className="text-lg font-semibold text-gray-900">Endereço</h2>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* CEP, Logradouro, Número */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-2">
                  <FormInput
                    name="cep"
                    control={control}
                    id="cep"
                    label="CEP"
                    type="text"
                    maxLength={10}
                    maskFn={PostalCodeMask}
                    placeholder="00000-000"
                  />
                </div>
                <div className="md:col-span-8">
                  <FormInput
                    name="logradouro"
                    control={control}
                    id="logradouro"
                    label="Rua/Avenida"
                    type="text"
                    placeholder="Nome da rua ou avenida"
                  />
                </div>
                <div className="md:col-span-2">
                  <FormInput
                    name="numero"
                    control={control}
                    id="numero"
                    label="Número"
                    type="text"
                    placeholder="123"
                  />
                </div>
              </div>

              {/* Complemento, Bairro */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormInput
                    name="complemento"
                    control={control}
                    id="complemento"
                    label="Complemento"
                    type="text"
                    placeholder="Apartamento, bloco, etc."
                  />
                </div>
                <div>
                  <FormInput
                    name="bairro"
                    control={control}
                    id="bairro"
                    label="Bairro"
                    type="text"
                    placeholder="Nome do bairro"
                  />
                </div>
              </div>

              {/* Município, UF */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-3">
                  <FormInput
                    name="municipio"
                    control={control}
                    id="municipio"
                    label="Município"
                    type="text"
                    placeholder="Nome da cidade"
                  />
                </div>
                <div>
                  <FormSelect
                    name="uf"
                    control={control}
                    id="uf"
                    label="UF"
                    placeholder="UF"
                    options={[
                      { value: 'SP', label: 'SP' },
                      { value: 'RJ', label: 'RJ' },
                      { value: 'MG', label: 'MG' },
                      { value: 'RS', label: 'RS' },
                      { value: 'PR', label: 'PR' },
                      { value: 'SC', label: 'SC' },
                      { value: 'BA', label: 'BA' },
                      { value: 'GO', label: 'GO' },
                      { value: 'PE', label: 'PE' },
                      { value: 'CE', label: 'CE' },
                      { value: 'MA', label: 'MA' },
                      { value: 'PB', label: 'PB' },
                      { value: 'PA', label: 'PA' },
                      { value: 'ES', label: 'ES' },
                      { value: 'PI', label: 'PI' },
                      { value: 'AL', label: 'AL' },
                      { value: 'RN', label: 'RN' },
                      { value: 'MT', label: 'MT' },
                      { value: 'MS', label: 'MS' },
                      { value: 'DF', label: 'DF' },
                      { value: 'SE', label: 'SE' },
                      { value: 'AM', label: 'AM' },
                      { value: 'RO', label: 'RO' },
                      { value: 'AC', label: 'AC' },
                      { value: 'AP', label: 'AP' },
                      { value: 'RR', label: 'RR' },
                      { value: 'TO', label: 'TO' }
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Observations Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faFileText} className="text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Observações</h2>
              </div>
            </div>

            <div className="p-6">
              <FormTextarea
                name="obs"
                control={control}
                id="obs"
                label="Observações"
                rows={4}
                placeholder="Observações adicionais sobre o paciente..."
              />
            </div>
          </div>

          {/* Error Display */}
          {error && <Alert variant="error" title="Erro ao salvar paciente" message={error} />}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting || !isValid}
                variant={pendingQueueData ? 'secondary' : 'primary'}
                leftIcon={!isSubmitting ? faSave : undefined}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>

              <Button
                type="button"
                onClick={handleSaveAndBack}
                disabled={isSubmitting || !isValid}
                variant="success"
                leftIcon={!isSubmitting ? faSave : undefined}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar e Voltar'}
              </Button>

              {pendingQueueData && (
                <Button
                  type="button"
                  onClick={handleSaveAndAddToQueue}
                  disabled={isSubmitting || !isValid}
                  variant="primary"
                  leftIcon={!isSubmitting ? faUserPlus : undefined}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar e Adicionar à Fila'}
                </Button>
              )}

              <Button type="button" onClick={handleBack} variant="secondary" leftIcon={faArrowLeft}>
                Voltar sem Salvar
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Notification Modal */}
      {notification && (
        <NotificationModal
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </main>
  );
}
